import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";
import {
  captureInitialAuthErrorFromUrl,
  getAuthErrorFromSearchParams,
  persistAuthError,
} from "../utils/authErrors";
import { reportAuthFailure } from "../utils/sentry";

/**
 * Global auth error capture — runs on every route inside Auth0Provider.
 */
export default function AuthErrorCapture() {
  const { error: auth0SdkError, isAuthenticated, isLoading } = useAuth0();
  const location = useLocation();
  const reportedRef = useRef(null);

  useEffect(() => {
    captureInitialAuthErrorFromUrl();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromUrl = getAuthErrorFromSearchParams(params);
    if (fromUrl) {
      persistAuthError(fromUrl, { source: "route_search", path: location.pathname });
    }
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (!auth0SdkError?.message) return;
    persistAuthError(auth0SdkError.message, {
      source: "auth0_sdk",
      name: auth0SdkError.name,
    });
    if (reportedRef.current === auth0SdkError.message) return;
    reportedRef.current = auth0SdkError.message;
    reportAuthFailure("auth0_sdk", auth0SdkError.message);
  }, [auth0SdkError]);

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    // Log when we land unauthenticated (helps debug redirect loops in console).
    console.info("[auth] Not signed in", { path: location.pathname });
  }, [isAuthenticated, isLoading, location.pathname]);

  return null;
}
